/** Shared limits and URI admission used by live capture and queued delivery. */

export const SESSION_SUMMARY_MAX_BODY_BYTES = 65_536;

/**
 * WorkEpisode admits twelve named tools plus the two deterministic tail
 * buckets (`__other__` and `__other_tools__`).
 */
export const SESSION_WORK_EPISODE_MAX_TOOL_KEYS = 14;
export const SESSION_SUMMARY_MAX_NAMED_TOOL_KEYS = 12;

const SENSITIVE_URI_QUERY_KEYS = new Set([
  "token",
  "secret",
  "signature",
  "sig",
  "credential",
  "password",
  "passcode",
  "authorization",
  "auth",
  "jwt",
  "code",
  "apikey",
]);

function sensitiveUriParameter(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]+/g, "_");
  const segments = normalized.split("_").filter(Boolean);
  return (
    SENSITIVE_URI_QUERY_KEYS.has(normalized) ||
    segments.some((segment) => SENSITIVE_URI_QUERY_KEYS.has(segment)) ||
    /^(?:api|access|secret|private|session)?key(?:id)?$/.test(normalized)
  );
}

/** Keep durable references followable without persisting URL credentials. */
export function isCredentialFreeHttpsUri(value: string): boolean {
  try {
    const parsed = new URL(value);
    const parameterKeys = [
      ...parsed.searchParams.keys(),
      ...(parsed.hash.includes("=")
        ? new URLSearchParams(parsed.hash.slice(1)).keys()
        : []),
    ];
    return (
      parsed.protocol === "https:" &&
      parsed.username.length === 0 &&
      parsed.password.length === 0 &&
      parameterKeys.every((key) => !sensitiveUriParameter(key))
    );
  } catch {
    return false;
  }
}

/** Standalone equivalent embedded into the generated runtime hook. */
export function buildCredentialFreeHttpsUriHookFragment(): string {
  return `
var SENSITIVE_URI_QUERY_KEYS = {
  token: true,
  secret: true,
  signature: true,
  sig: true,
  credential: true,
  password: true,
  passcode: true,
  authorization: true,
  auth: true,
  jwt: true,
  code: true,
  apikey: true,
};

function sensitiveUriParameter(key) {
  var normalized = String(key).toLowerCase().replace(/[^a-z0-9]+/g, "_");
  var segments = normalized.split("_").filter(Boolean);
  if (SENSITIVE_URI_QUERY_KEYS[normalized]) return true;
  for (var i = 0; i < segments.length; i++) {
    if (SENSITIVE_URI_QUERY_KEYS[segments[i]]) return true;
  }
  return /^(?:api|access|secret|private|session)?key(?:id)?$/.test(normalized);
}

function isCredentialFreeHttpsUri(value) {
  try {
    var parsed = new URL(value);
    if (parsed.protocol !== "https:" || parsed.username || parsed.password) return false;
    var keys = [];
    parsed.searchParams.forEach(function (_value, key) { keys.push(key); });
    if (parsed.hash.indexOf("=") >= 0) {
      new URLSearchParams(parsed.hash.slice(1)).forEach(function (_value, key) { keys.push(key); });
    }
    for (var i = 0; i < keys.length; i++) {
      if (sensitiveUriParameter(keys[i])) return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

`;
}
